import get from 'lodash/get'

import { NotificationBadge } from '@/services/notification'
import { gamification_enabled } from '@/lib/remote-config/constants'

import userMixin from './user'
import remoteConfigMixin from './remoteConfig'

export default {
  mixins: [userMixin, remoteConfigMixin],
  computed: {
    _lessonStepsById() {
      return this.$store.state.lesson.lessonStep.byId
    },
    acquiredBadges() {
      return get(this.user, ['acquiredBadges'], [])
    }
  },
  methods: {
    _gamificationEnabled() {
      return this.remoteConfig.getBoolean(gamification_enabled)
    },
    /**
     * Get a badge from its id
     * @param {string} badgeId
     */
    getBadge(badgeId) {
      return this.$store.getters.getBadgeById(badgeId)
    },
    /**
     * Test if the user did success to unlock a quiz badge
     * @param {number} quizResult
     * @returns {boolean}
     */
    shouldUnlockQuizBadge(quizResult) {
      return quizResult >= 50
    },
    /**
     * Unlock the badge attached to the given quiz
     * @param {string} quizId
     */
    unlockQuizCompletionBadge(quizId) {
      // Anonymous access doesn't allow badge unlocking
      if (!this.user || !this._gamificationEnabled()) return

      const badge = this._getBadgeFromQuiz(quizId)

      this._unlockBadge(badge)
    },
    /**
     * Unlock the badge attached to the given masterclass
     * @param {string} masterclassId
     */
    unlockMasterclassCompletionBadge(masterclassId) {
      // Anonymous access doesn't allow badge unlocking
      if (!this.user || !this._gamificationEnabled()) return

      const badge = this._getBadgeFromMasterclass(masterclassId)

      this._unlockBadge(badge)
    },
    /**
     * Unlock the given badge if not already acquired
     * @param {Badge} badge
     */
    _unlockBadge(badge) {
      if (!badge || !this._gamificationEnabled()) return

      // Badge already unlocked
      if (this.acquiredBadges.includes(badge.id)) return

      this._triggerNotificationForBadge(badge.id)

      const acquiredBadges = [...this.acquiredBadges, badge.id]

      return this.updateUserInformations({ acquiredBadges })
    },
    /**
     * Get the badge associated to a quiz
     * @param {string} quizId
     * @returns {Badge}
     */
    _getBadgeFromQuiz(quizId) {
      const lessonAssociatedToQuiz = Object.values(this._lessonStepsById).find(
        lessonStep => lessonStep.quizId === quizId
      )

      const badgeId = get(lessonAssociatedToQuiz, ['badgeId'], null)

      if (!badgeId) return null

      return this.getBadge(badgeId)
    },
    /**
     * Get the badge associated to a masterclass
     * @param {string} masterclassId
     * @return {Badge}
     */
    _getBadgeFromMasterclass(masterclassId) {
      // Masterclass id is used as quiz identifier for the masterclass quiz
      const masterclassAssociatedToQuiz = this.$store.getters.getThemeById(masterclassId)

      const badgeId = get(masterclassAssociatedToQuiz, ['badgeId'], null)

      if (!badgeId) return null

      return this.getBadge(badgeId)
    },
    /**
     * Trigger an in-app notification for a badge unlocked
     * @param {string} badgeId
     */
    _triggerNotificationForBadge(badgeId) {
      const badge = this.getBadge(badgeId)

      if (!badge) return

      const notification = NotificationBadge({
        title: this.$t('progression.unlockTitle'),
        message: `"${ this.$t('progression.unlock') }" "${badge.title}"`,
        image: badge.image,
        badgeId
      })

      this.$addNotification(notification)
    }
  }
}
